// src/newDashboard/admin/AdminPillStates.jsx
//
// "Pills by state": choose which states each pill shows in, in the app
// and on the owner/charity event form. Saved to config/pillStates.
// A pill set to "Everywhere" shows in every state.

import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import {
  ALL_PILL_GROUPS,
  DEFAULT_PILL_STATES,
  STATES,
  mergePillStates,
  pillAllowed,
  setCachedPillStates,
} from "../shared/pillCatalog";

const CONFIG_REF = doc(db, "config", "pillStates");
const STATE_NAME = Object.fromEntries(STATES);

const card = {
  background: "#15121F",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  padding: 16,
  marginBottom: 14,
};
const muted = { color: "#B8B0C8", fontSize: 15, lineHeight: 1.5 };

function btn(bg) {
  return {
    background: bg,
    border: "none",
    padding: "9px 16px",
    borderRadius: 8,
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 15,
  };
}

export default function AdminPillStates() {
  const [map, setMap] = useState(DEFAULT_PILL_STATES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(""); // pill being edited
  const [preview, setPreview] = useState("MN");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(CONFIG_REF);
        setMap(mergePillStates(snap.exists() ? snap.data().pills : null));
      } catch (e) {
        setMsg("Couldn't load saved settings. Showing the defaults.");
      }
      setLoading(false);
    })();
  }, []);

  const setPill = (pill, states) => {
    setMap((prev) => ({ ...prev, [pill]: states }));
    setMsg("");
  };

  const toggleState = (pill, code) => {
    const now = map[pill] || [];
    setPill(pill, now.includes(code) ? now.filter((c) => c !== code) : [...now, code].sort());
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      // Save every pill's list, so the app and dashboard always agree.
      const pills = {};
      Object.values(ALL_PILL_GROUPS).flat().forEach((p) => {
        pills[p] = map[p] || [];
      });
      pills["Pull-tab / E-tab Updates"] = map["Pull-tab / E-tab Updates"] || map["Pull-tabs"] || [];
      await setDoc(CONFIG_REF, { pills, updatedAt: new Date() }, { merge: false });
      setCachedPillStates(mergePillStates(pills));
      setMsg("Saved! The app picks this up the next time someone opens it.");
    } catch (e) {
      setMsg("Couldn't save. Check your connection and try again.");
    }
    setSaving(false);
  };

  const resetDefaults = () => {
    setMap({ ...DEFAULT_PILL_STATES });
    setMsg("Defaults put back. Click Save to keep them.");
  };

  const previewList = useMemo(
    () =>
      Object.entries(ALL_PILL_GROUPS).map(([group, pills]) => [
        group,
        pills.filter((p) => pillAllowed(p, preview, map)),
      ]),
    [preview, map]
  );

  if (loading) return <div style={{ color: "#fff" }}>Loading…</div>;

  return (
    <div style={{ color: "#fff", maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>Pills by state</h2>
      <p style={muted}>
        Choose where each pill shows up. "Everywhere" pills (like Trivia) show in every state. Regional
        pills (like Crawfish Boil or Meat Raffle) only show in the states you check. This controls the
        customer app's pills and alert choices, and the pills bars see when they add an event. The app
        also hides a pill when nothing near the person has it yet.
      </p>

      <div style={card}>
        <strong style={{ fontSize: 17 }}>See what a state gets</strong>
        <div style={{ margin: "10px 0" }}>
          <select
            value={preview}
            onChange={(e) => setPreview(e.target.value)}
            style={{ padding: 10, borderRadius: 8, border: "none", fontSize: 15 }}
          >
            {STATES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
        {previewList.map(([group, pills]) => (
          <p key={group} style={{ ...muted, margin: "4px 0" }}>
            <strong style={{ color: "#fff" }}>{group}:</strong> {pills.length ? pills.join(" · ") : "none"}
          </p>
        ))}
      </div>

      {Object.entries(ALL_PILL_GROUPS).map(([group, pills]) => (
        <div key={group} style={card}>
          <strong style={{ fontSize: 17 }}>{group}</strong>
          {pills.map((pill) => {
            const states = map[pill] || [];
            const everywhere = states.length === 0;
            const isOpen = open === pill;
            return (
              <div key={pill} style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "10px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 16, minWidth: 190 }}>{pill}</span>
                  <span style={{ ...muted, flex: 1 }}>
                    {everywhere ? "Everywhere" : `${states.length} state${states.length === 1 ? "" : "s"}: ${states.join(", ")}`}
                  </span>
                  <button type="button" onClick={() => setOpen(isOpen ? "" : pill)} style={btn("#3B2F5C")}>
                    {isOpen ? "Done" : "Change"}
                  </button>
                </div>
                {isOpen && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      <button type="button" onClick={() => setPill(pill, [])} style={btn("#3B2F5C")}>
                        Everywhere
                      </button>
                      <button
                        type="button"
                        onClick={() => setPill(pill, [...(DEFAULT_PILL_STATES[pill] || [])])}
                        style={btn("#3B2F5C")}
                      >
                        Back to default
                      </button>
                    </div>
                    <p style={{ ...muted, margin: "0 0 8px" }}>
                      Check the states it belongs in. Nothing checked = Everywhere.
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                        gap: 6,
                      }}
                    >
                      {STATES.map(([code]) => (
                        <label key={code} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 15, cursor: "pointer" }}>
                          <input type="checkbox" checked={states.includes(code)} onChange={() => toggleState(pill, code)} />
                          {STATE_NAME[code]}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "#0B0B12",
          padding: "14px 0",
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <button type="button" onClick={save} disabled={saving} style={btn("#A855F7")}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button type="button" onClick={resetDefaults} style={btn("#3B2F5C")}>
          Put back all defaults
        </button>
        {msg && <span style={{ color: msg.startsWith("Saved") ? "#4ADE80" : "#FBBF24" }}>{msg}</span>}
      </div>
    </div>
  );
}
